import React, { useState } from 'react';
import Page from 'components/Page';
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, List, Row, Select, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import queryString from 'query-string';
import { useLazyQuery, useMutation } from 'hooks/apollo';
import useGlobalContext from 'hooks/useGlobalContext';
import { downloadUrl } from 'shared/utils/download';

import { gql } from '@apollo/client';
import importMutation from './importSurveyResponsesMutation.gql';

const exportFormats = [
  { label: 'JSON', value: 'JSON' },
  // { label: 'CSV', value: 'CSV' },
  // { label: 'Excel', value: 'XLSX' },
];

const EXPORT_FROM_CLICKHOUSE = gql`
  query exportFromClickhouse($input: ExportInput!) {
    exportFromClickhouse(input: $input) {
      rows
      files
    }
  }
`;

const IMPORT_MATOMO_VISITS = gql`
  mutation importMatomoVisits($input: ImportMatomoVisitsInput!) {
    importMatomoVisits(input: $input)
  }
`;

const initialValues = {
  query: 'SELECT * FROM matomo.matomo_visits where idsite = 476',
  nestedQuery: 'SELECT * FROM matomo.matomo_actions where idsite = 476',
  joinColumn: 'idvisit',
  nestedJoinColumn: 'idvisit',
  nestedColumnName: 'actions',
  format: 'JSON',
  skipNullValues: true,
};

const DataTools = () => {
  const [mutate, { loading }] = useMutation(importMutation);
  const { message } = useGlobalContext();
  const [result, setResult] = useState(null);
  const [exportFromClickhouse, exportFromClickhouseResult] = useLazyQuery(EXPORT_FROM_CLICKHOUSE);
  const [importMatomoVisits, importMatomoVisitsResults] = useMutation(IMPORT_MATOMO_VISITS);

  const onDownloadSurveyResponses = (values) => {
    const url = queryString.stringifyUrl({ url: `${process.env.MANAGER_BASE_PATH}/download/surveyResponses.xlsx`, query: values }, { strict: false });
    downloadUrl(url, 'surveyResponses.xlsx');
  };

  const onDownloadSurveyQuestions = (values) => {
    const url = queryString.stringifyUrl({ url: `${process.env.MANAGER_BASE_PATH}/download/surveyQuestions.xlsx`, query: values });
    downloadUrl(url, 'surveyQuestions.xlsx');
  };

  const onExportFromClickhouse = (values) => {
    exportFromClickhouse({ variables: { input: values } });
  };

  const onImportMatomoVisits = (values) => {
    importMatomoVisits({ variables: { input: values } });
  };

  const upload = {
    name: 'file',
    showUploadList: false,
    beforeUpload: async file => {
      const isXls = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      if (!isXls) {
        message.error(`${file.name} is not an Excel file`);
      } else {
        try {
          const { data } = await mutate({ variables: { file } });
          setResult(data.importSurveyResponses);
        } catch (e) {
          message.error(`Something went wrong. ${e.message}`);
        }
      }

      return false;
    },
  };

  return (
    <Page>
      <Page.Header
        title="Data tools"
      />
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Export data from Clickhouse">
            <Form layout="vertical" onFinish={onExportFromClickhouse} initialValues={initialValues}>
              <Form.Item label="Query" name="query" rules={[{ required: true }]}>
                <Input.TextArea />
              </Form.Item>
              <Form.Item label="Nested query" name="nestedQuery">
                <Input.TextArea />
              </Form.Item>
              <Form.Item label="Join column" name="joinColumn">
                <Input />
              </Form.Item>
              <Form.Item label="Nested join column" name="nestedJoinColumn">
                <Input />
              </Form.Item>
              <Form.Item label="Column name" name="nestedColumnName">
                <Input />
              </Form.Item>
              <Form.Item label="Format" name="format">
                <Select placeholder="Format" name="format" style={{ width: '100%' }} options={exportFormats} />
              </Form.Item>
              <Form.Item name="skipNullValues" valuePropName="checked">
                <Checkbox>
                  Skip null values
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={exportFromClickhouseResult.loading} icon={<DownloadOutlined />}>
                  Export
                </Button>
              </Form.Item>
            </Form>
            {exportFromClickhouseResult.data && !exportFromClickhouseResult.loading && (
              <List
                header="Files"
                footer={(
                  <div>
                    Total rows:
                    {exportFromClickhouseResult.data.exportFromClickhouse.rows}
                  </div>
                )}
                bordered
                dataSource={exportFromClickhouseResult.data.exportFromClickhouse.files}
                renderItem={(item) => (
                  <List.Item>
                    <a href={item} target="_blank" rel="noreferrer">{item}</a>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
        <Col span={24}>
          <Card title="Import matomo visits">
            <Form layout="vertical" onFinish={onImportMatomoVisits}>
              <Form.Item label="Target matomo id" name="targetMatomoId" rules={[{ required: true }]}>
                <InputNumber />
              </Form.Item>
              <Form.Item label="Source folder" name="sourceFolder" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={importMatomoVisitsResults.loading} icon={<UploadOutlined />}>
                  Import
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col span={24}>
          <Card title="Export questions to Excel">
            <Form layout="vertical" onFinish={onDownloadSurveyQuestions} initialValues={{ where: '{ "survey": { "id": "<survey id>" } }' }}>
              <Form.Item label="Filter" name="where" rules={[{ required: true }]}>
                <Input.TextArea />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  <DownloadOutlined />
                  Download survey questions
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col span={24}>
          <Card title="Export survey responses to Excel">
            <Form layout="vertical" onFinish={onDownloadSurveyResponses} initialValues={{ where: '{ "survey": { "id": "<survey id>" } }', questionFields: 'title', optionFields: 'title' }}>
              <Form.Item label="Survey response filter" name="where" rules={[{ required: true }]}>
                <Input.TextArea />
              </Form.Item>
              <Form.Item label="Survey response fields" name="surveyResponseFields">
                <Input />
              </Form.Item>
              <Form.Item label="Question response filter" name="questionResponsesWhere">
                <Input.TextArea />
              </Form.Item>
              <Form.Item label="Question fields" name="questionFields">
                <Input />
              </Form.Item>
              <Form.Item label="Option fields" name="optionFields">
                <Input />
              </Form.Item>
              <Form.Item label="Order by" name="orderBy">
                <Input />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  <DownloadOutlined />
                  Download survey responses
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
        <Col span={24}>
          <Card title="Import survey responses from Excel">
            <Upload {...upload}>
              <Button type="primary" loading={loading}>
                <UploadOutlined />
                Select file
              </Button>
            </Upload>
            <pre>{result && JSON.stringify(result, null, 2)}</pre>
          </Card>
        </Col>
      </Row>
    </Page>
  );
};

export default DataTools;
